import {
  Box,
  Chip,
  Paper,
  Theme,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import MenuBookIcon from "@material-ui/icons/MenuBook";
import PlayCircleFilledIcon from "@material-ui/icons/PlayCircleFilled";
import { useAppSelector } from "app/hooks";
import { selectLessonList } from "features/Admin/pages/ManagerLesson/managerLessonSlice";
import { Lesson } from "models/managerLesson";
import queryString from "query-string";
import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

export interface ListVideoRecommendProps {
  openDrawer: boolean;
  setOpenDrawer: (e: any) => void;
}

const DEFAULT_FALLBACK_THUMB =
  "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&auto=format&fit=crop&q=80";

const useStyles = makeStyles((theme: Theme) => ({
  cardContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 20,
    border: "1px solid #E2E8F0",
    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.04)",
    padding: 20,
    overflow: "hidden",
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: 16,
    marginBottom: 16,
    borderBottom: "1px solid #F1F5F9",
  },
  headerTitle: {
    fontWeight: 900,
    fontSize: "1rem",
    color: "#0F172A",
    letterSpacing: "0.01em",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  itemCard: {
    backgroundColor: "#F8FAFC",
    borderRadius: 16,
    border: "1px solid #E2E8F0",
    padding: 12,
    marginBottom: 12,
    display: "flex",
    gap: 12,
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#FFFFFF",
      borderColor: "#C7D2FE",
      transform: "translateY(-2px)",
      boxShadow: "0 8px 20px rgba(79, 70, 229, 0.12)",
      "& $itemTitle": {
        color: "#4F46E5",
      },
    },
  },
  thumbWrapper: {
    position: "relative",
    width: 100,
    height: 72,
    borderRadius: 12,
    overflow: "hidden",
    flexShrink: 0,
    backgroundColor: "#0F172A",
  },
  thumbImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  playOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(15, 23, 42, 0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#FFFFFF",
    opacity: 0.9,
  },
  itemContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    flex: 1,
    minWidth: 0,
  },
  itemCategory: {
    fontSize: "0.7rem",
    fontWeight: 900,
    color: "#4F46E5",
    backgroundColor: "#EEF2FF",
    padding: "2px 8px",
    borderRadius: 6,
    display: "inline-block",
    maxWidth: "fit-content",
    textTransform: "uppercase",
  },
  itemTitle: {
    fontSize: "0.85rem",
    fontWeight: 800,
    color: "#0F172A",
    lineHeight: 1.3,
    marginTop: 4,
    marginBottom: 4,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    transition: "color 0.2s ease",
  },
  metaRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontSize: "0.75rem",
    color: "#64748B",
  },
}));

export default function ListVideoRecommend({
  openDrawer,
  setOpenDrawer,
}: ListVideoRecommendProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const listLesson = useAppSelector(selectLessonList);
  const location: any = useLocation();
  const querySearchDate = queryString.parse(location.search);
  const classes = useStyles();

  const rawItems: Lesson[] = listLesson?.content || [];

  return (
    <Paper className={classes.cardContainer} elevation={0}>
      <Box className={classes.headerRow}>
        <Typography className={classes.headerTitle}>
          <MenuBookIcon style={{ fontSize: 20, color: "#4F46E5" }} />
          {t("home.detaillesson.danhsachbaihockhac") || "DANH SÁCH BÀI HỌC KHÁC"}
        </Typography>
        <Chip
          label={`${rawItems.length} BÀI HỌC`}
          size="small"
          style={{
            backgroundColor: "#F1F5F9",
            color: "#475569",
            fontWeight: 800,
            fontSize: "0.7rem",
          }}
        />
      </Box>

      {rawItems.map((lesson: Lesson) => {
        const pubDateStr = String(lesson?.publishDate || "");
        const day = pubDateStr ? pubDateStr.slice(-2) : "";
        const month = pubDateStr ? pubDateStr.slice(4, 6) : "";
        const year = pubDateStr ? pubDateStr.slice(0, 4) : "";

        const thumb =
          lesson?.attachment?.urlThumbnail ||
          lesson?.powerPointFileList?.[0]?.powerPointFile?.urlThumbnail ||
          DEFAULT_FALLBACK_THUMB;

        return (
          <Box
            key={lesson.id}
            className={classes.itemCard}
            onClick={() =>
              navigate({
                pathname: `/admin/manager-lesson/listPosted/detail/${lesson?.id}`,
                search: `year=${querySearchDate.year || ""}&month=${querySearchDate.month || ""}`,
              })
            }
          >
            {/* Thumbnail */}
            <Box className={classes.thumbWrapper}>
              <img
                src={thumb}
                alt="Thumbnail"
                className={classes.thumbImg}
                onError={(e: any) => {
                  e.target.onerror = null;
                  e.target.src = DEFAULT_FALLBACK_THUMB;
                }}
              />
              <Box className={classes.playOverlay}>
                <PlayCircleFilledIcon style={{ fontSize: 24 }} />
              </Box>
            </Box>

            {/* Content */}
            <Box className={classes.itemContent}>
              <Box className={classes.itemCategory}>
                {lesson?.lessionCategory?.name || "BÀI HỌC"}
              </Box>

              <Tooltip title={lesson?.lessionName || ""}>
                <Typography className={classes.itemTitle}>
                  {lesson?.lessionName}
                </Typography>
              </Tooltip>

              <Box className={classes.metaRow}>
                {pubDateStr && (
                  <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                    <CalendarTodayIcon style={{ fontSize: 12, color: "#94A3B8" }} />
                    <span>
                      {day}/{month}/{year}
                    </span>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        );
      })}
    </Paper>
  );
}
